import React from 'react';
import CgPageLoader from 'modules/Common/layout/CgPageLoader';
import { CgSkeletonBar } from 'modules/Common/ui/CgSkeletons';

// The free-supplier portal's loading vocabulary. Nothing here is a new
// primitive — every export composes the two the app already ships:
//
//   • CgPageLoader   (Common/CgPageLoader) — the brand-coloured progress pill
//     with a ROTATING status line. Used where the wait is a real fetch with no
//     meaningful silhouette to mirror, and the honest thing to show is *what*
//     we're fetching rather than a fake shape.
//   • CgSkeletonBar  (Common/CgSkeletons)  — the slate pulse bar those
//     skeletons are built from. Used where the final layout's silhouette IS
//     known, so the page keeps its geometry instead of collapsing to a line of
//     centred text and then jumping.
//
// Which to reach for: if the user is about to see a LIST or a FORM whose shape
// we already know, use a skeleton (no layout shift). If they're waiting on a
// single record or a settings blob, use SupplierPanelLoader and tell them what
// is being loaded.

// The portal's standard surface card — every page's panels use this exact
// chrome, so a loading panel that borrows it doesn't read as a different box.
const PANEL_CLASS = 'rounded-2xl border border-slate-200 bg-white';

/**
 * Full-panel loader for a real fetch: CgPageLoader inside the portal's card
 * chrome, with a task-specific rotating status line instead of "Loading…".
 *
 * @param {string[]} messages   What we're actually waiting on, in order. Keep
 *                              them concrete ("Fetching the declarant's
 *                              request") — the rotation is the whole point.
 * @param {string}   ariaLabel  Announced to screen readers (CgPageLoader puts
 *                              it on its role="status" region).
 * @param {number}   minHeight  Stage height; defaults to the ~200px that
 *                              matches the p-10 placeholders these replace.
 * @param {boolean}  bare       Skip the card chrome (host already has one).
 */
export function SupplierPanelLoader({
  messages,
  ariaLabel = 'Loading',
  minHeight = 200,
  bare = false,
  className = '',
}) {
  const loader = (
    <CgPageLoader
      statusMessages={messages}
      ariaLabel={ariaLabel}
      minHeight={minHeight}
    />
  );
  if (bare) return loader;
  return <div className={`${PANEL_CLASS} ${className}`}>{loader}</div>;
}

/**
 * Getting-started skeleton: the hero card, the progress rail, and `steps`
 * checklist rows — the same geometry GettingStartedPage renders once the
 * checklist queries resolve. Without this the page paints a confident
 * "0 of 9 steps completed" with every step un-ticked, then silently corrects
 * itself; a supplier who has already finished six steps sees their progress
 * wiped and restored on every load.
 */
export function SupplierChecklistSkeleton({ steps = 9 }) {
  return (
    <div
      className="flex flex-col gap-6"
      aria-hidden="true"
    >
      {/* Hero — eyebrow, headline, blurb, then the progress rail. */}
      <div className={`${PANEL_CLASS} p-6`}>
        <CgSkeletonBar className="h-2.5 w-40" />
        <CgSkeletonBar className="h-6 w-full max-w-lg mt-2" />
        <CgSkeletonBar className="h-3 w-full max-w-xl mt-3" />
        <CgSkeletonBar className="h-3 w-3/4 max-w-md mt-1.5" />

        <div className="mt-5">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 mb-1.5">
            <CgSkeletonBar className="h-3 w-36" />
            <CgSkeletonBar className="h-3 w-24" />
          </div>
          {/* Same `flex gap-1` segmented rail the real progress bar uses, so
              the tick marks don't shift when the real one takes over. */}
          <div className="flex gap-1">
            {Array.from({ length: steps }).map((_, i) => (
              <div
                key={i}
                className="h-1.5 flex-1 rounded-full bg-slate-200 animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Checklist rows — numbered bullet, title + hint, CTA pill. */}
      <div className={`${PANEL_CLASS} divide-y divide-slate-100`}>
        {Array.from({ length: steps }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-4 px-5 py-4"
          >
            <div className="w-7 h-7 rounded-full bg-slate-100 ring-1 ring-slate-200 shrink-0 animate-pulse" />
            <div className="flex-1 min-w-0">
              <CgSkeletonBar className="h-3.5 w-52 max-w-full" />
              <CgSkeletonBar className="h-2.5 w-72 max-w-full mt-2" />
            </div>
            <div className="h-[30px] w-28 rounded-xl bg-slate-200 animate-pulse shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Grouped-card list skeleton — the drafts inbox and the requests inbox both
 * render `section` cards with a tinted header strip and N rows beneath.
 */
export function SupplierCardListSkeleton({ cards = 2, rowsPerCard = 3 }) {
  return (
    <div
      className="flex flex-col gap-4"
      aria-hidden="true"
    >
      {Array.from({ length: cards }).map((_, cardIdx) => (
        <section
          key={cardIdx}
          className={`${PANEL_CLASS} overflow-hidden`}
        >
          <div className="px-4 py-3 bg-slate-50/60 border-b border-slate-100 flex items-center gap-2">
            <div className="w-3.5 h-3.5 rounded bg-slate-200 animate-pulse shrink-0" />
            <CgSkeletonBar className="h-3 w-44 max-w-full" />
          </div>
          {Array.from({ length: rowsPerCard }).map((_, rowIdx) => (
            <div
              key={rowIdx}
              className="flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-b-0"
            >
              <div className="flex-1 min-w-0">
                <CgSkeletonBar className="h-3.5 w-48 max-w-full" />
                <CgSkeletonBar className="h-2.5 w-64 max-w-full mt-2" />
              </div>
              {/* The three right-aligned stats (direct / indirect / total). */}
              <div className="hidden sm:flex items-center gap-3 shrink-0">
                {Array.from({ length: 3 }).map((_, statIdx) => (
                  <div key={statIdx}>
                    <CgSkeletonBar className="h-2 w-12" />
                    <CgSkeletonBar className="h-3 w-14 mt-1.5" />
                  </div>
                ))}
              </div>
              <div className="h-5 w-20 rounded-full bg-slate-200 animate-pulse shrink-0" />
            </div>
          ))}
        </section>
      ))}
    </div>
  );
}

/**
 * Form-page skeleton — `sections` cards, each a heading over a two-column
 * grid of label/input pairs (CompanyPage, and the installation editor).
 */
export function SupplierFormSkeleton({ sections = 2, fieldsPerSection = 4 }) {
  return (
    <div
      className="flex flex-col gap-6"
      aria-hidden="true"
    >
      {Array.from({ length: sections }).map((_, sectionIdx) => (
        <div
          key={sectionIdx}
          className={`${PANEL_CLASS} p-6`}
        >
          <CgSkeletonBar className="h-4 w-40" />
          <CgSkeletonBar className="h-2.5 w-64 max-w-full mt-2 mb-5" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            {Array.from({ length: fieldsPerSection }).map((_, fieldIdx) => (
              <div key={fieldIdx}>
                <CgSkeletonBar className="h-2.5 w-24 mb-2" />
                <div className="h-9 w-full rounded-xl bg-slate-100 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Revision-history skeleton — the modal's vertical timeline of revisions.
 */
export function SupplierHistorySkeleton({ rows = 3 }) {
  return (
    <div
      className="py-2"
      aria-hidden="true"
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-b-0"
        >
          <div className="w-2 h-2 rounded-full bg-slate-200 animate-pulse shrink-0 mt-1.5" />
          <div className="flex-1 min-w-0">
            <CgSkeletonBar className="h-3 w-32" />
            <CgSkeletonBar className="h-2.5 w-52 max-w-full mt-2" />
          </div>
          <CgSkeletonBar className="h-3 w-16 shrink-0" />
        </div>
      ))}
    </div>
  );
}
